import { HostListener, inject, Injectable } from "@angular/core";
import { OidcSecurityService } from "angular-auth-oidc-client";

import { CustomWindow } from "@core/models/custom-window";
import { JwtToken } from "@core/models/jwt-token";
import { ApiCacheService, GlobalService } from "@shared/services";
import { timer } from "rxjs";

declare let window: CustomWindow;

@Injectable({
  providedIn: "root",
})
export class CoreService {
  oidcService = inject(OidcSecurityService);
  apiCacheService = inject(ApiCacheService);
  globalService = inject(GlobalService);

  @HostListener("window:resize", ["$event"])
  onResize(event: any): void {
    this.initDeviceType(event.target.innerWidth);
  }

  /**
   * Initialize core services
   * - Auth user and set decoded token to global service signal
   * - Init window resize listener
   * - Init injected props
   *   - Init theme
   * - Set all global service signals
   */
  init(): void {
    this.initAuth();
    this.initDeviceType();
    this.initInjectedProps();
    this.apiCacheService.startCacheClearTimer();
  }

  // Auth user and set decoded token to global service signal
  private initAuth(): void {
    const decodeToken = (token: string): JwtToken | null => {
      try {
        const payload = token.split(".")[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          window
            .atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );
        const decodedToken = JSON.parse(jsonPayload);
        this.globalService.authedToken.set(decodedToken);
        return decodedToken;
      } catch (error) {
        console.error("Failed to decode JWT", error);
        return null;
      }
    };

    const setAdminStatus = (decodeToken: JwtToken | null): void => {
      const roles = decodeToken?.role || [];
      const adminRoles = ["SQH-Admin", "Sequence History Admin"];
      if (roles.some((role: string) => adminRoles.includes(role))) {
        this.globalService.isAdmin.set(true);
      }
    };

    console.log("%cChecking Auth...", "color: lightblue");

    this.oidcService.checkAuth().subscribe((response) => {
      if (!response?.isAuthenticated) {
        this.oidcService.authorize();
      } else {
        const decodedToken = decodeToken(response.accessToken);
        console.log(
          `%cIs authenticated: ${response.isAuthenticated}`,
          "color: lightgreen",
        );
        setAdminStatus(decodedToken);
      }
    });
  }

  private initDeviceType(width: number = window.innerWidth): void {
    this.globalService.screenWidth.set(width);
    const deviceType =
      width < 440 ? "mobile" : width < 1200 ? "tablet" : "desktop";
    this.globalService.device.set(deviceType);
  }

  private initInjectedProps(): void {
    // Get theme if set from APA app.
    if (window.userTheme) {
      this.setTheme(window.userTheme);

      if (window.openedFromApaPilot) {
        this.globalService.isApaAppV3.set(true);
      }
    }

    const self = this;

    // Detect any future theme changes.
    Object.defineProperty(window, "userTheme", {
      set(value) {
        self.setTheme(value);
        console.log("userTheme", value);
      },
    });

    Object.defineProperty(window, "isOpenedFromApaApp", {
      set() {
        self.globalService.isApaAppV3.set(true);
      },
    });
  }

  private setTheme(theme: string): void {
    if (theme) {
      this.globalService.isApaApp.set(true);
    }

    if (this.globalService.theme() !== theme) {
      this.globalService.applyTheme(theme);
    }
  }
}
